import { useEffect } from "react";
import "./OrderPlaced.scss";
import { MotifIcon } from "@ey-xd/motif-react";
import { IconoirCheckCircle, IconoirArrowLeft } from "@ey-xd/motif-icon";
import { IconoirOpenNewWindow } from "@ey-xd/motif-icon";
import { SKU } from "../../../../store/slices/CheckoutSlice";
import Button from "../../../../components/Button/Button";
import { useNavigate } from "react-router";
import { ProductAccordian } from "../OrderExpected/ProductAccordian";
import BreadcrumbHome from "../../../../components/BreadcrumbHome";
import { getTotalOrderAmountRow, getCostType } from "../../CheckoutHelper";
import InfoTooltip from "../../../../components/InfoTooltip";
import { variableTotalInfo } from "../../../../utils/skuTable";
import {
  fetchOpportunityName,
  resetOpportunityNameState,
} from "../../../../store/slices/OpportunityNameSlice";
import { RootState, AppDispatch } from "../../../../store";
import { useSelector, useDispatch } from "react-redux";
import Loader from "../../../../components/Loader";

export interface OrderResponseDetails {
  OrderId: number;
  OrderStatus: string;
  Description: string;
  EngagementId: string;
  OpportunityId: string;
  Details: {
    ProductName: string;
    AssetAdvice: string;
    SkuDetails: SKU[];
  }[];
  CRMSyncFailureStatus: number | null;
}

interface OrderPlacedProps {
  orderData: OrderResponseDetails | null;
  configData: any;
}

export const OrderPlaced = ({ orderData, configData }: OrderPlacedProps) => {
  const nav = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    if (orderData && orderData.OpportunityId) {
      dispatch(
        fetchOpportunityName({
          Action: "getOpportunityName",
          opportunityCode: orderData?.OpportunityId || "",
        }),
      );
    }
    return () => {
      dispatch(resetOpportunityNameState());
    };
  }, [dispatch, orderData]);

  const opportunityNameInStore = useSelector(
    (state: RootState) => state.OpportunityNameSlice.data,
  );

  const opportunityName = Array.isArray(opportunityNameInStore)
    ? opportunityNameInStore[0]?.opportunityName
    : "";

  const opportunityNameLoading = useSelector(
    (state: RootState) => state.OpportunityNameSlice.isLoading,
  );

  const getTotalOrderAmount = (checkoutListData: OrderResponseDetails) => {
    if (!checkoutListData || checkoutListData === null) {
      return 0;
    }
    if (checkoutListData && checkoutListData !== null) {
      let totalAmount = 0;
      if (checkoutListData?.Details.length === 0) {
        return 0;
      }
      checkoutListData.Details.forEach((orderDataRow: any) => {
        orderDataRow.SkuDetails.forEach((sku: any) => {
          const quantity = sku.OrderQuantity || 0;
          const cost = sku.CostMetricCost || 0;
          const duration = sku.OrderInterval || 0;
          totalAmount += cost * quantity * duration;
          if (sku.SkuRelation && sku.SkuRelation.length > 0) {
            sku.SkuRelation.forEach((relation: any) => {
              const quantity = relation.OrderQuantity || 0;
              const cost = relation.CostMetricCost || 0;
              const duration = relation.OrderInterval || 0;
              totalAmount += cost * quantity * duration;
            });
          }
        });
      });
      return totalAmount.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
    }
  };

  const getTotalOrderAmountVariable = (
    checkoutListData: OrderResponseDetails,
  ) => {
    let totalAmount = 0;
    checkoutListData.Details.forEach((order: any) => {
      order.SkuDetails.forEach((sku: any) => {
        const costType = getCostType(sku.CostMetricCd, configData);
        if (!(costType === "Fixed")) {
          totalAmount += getTotalOrderAmountRow(sku);
        }
      });
    });
    return totalAmount;
  };

  const variableCost = orderData ? getTotalOrderAmountVariable(orderData) : 0;

  const breadcrumbItems = [
    {
      label: "Home",
      value: "Home",
      elementKey: "submission",
      text: "Home",
      url: `/`,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
    {
      label: "Order Confirmation",
      value: "Order Confirmation",
      elementKey: "submission",
      text: "Order Confirmation",
      url: `/marketplace`,
      disabled: true,
      icon: <MotifIcon src={IconoirArrowLeft} />,
    },
  ];

  if (!orderData || !orderData.Details || orderData.Details.length === 0) {
    return (
      <div className="motif-container order-placed-error">
        <div className="motif-row">
          <div className="motif-col-12">
            <div className="order-placed-error-text">
              No order data available.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="order-placed-header">
        <div className="product-cost-setup-bredcrumb">
          <BreadcrumbHome
            items={breadcrumbItems}
            ariaLabel="Breadcrumb navigation"
          />
        </div>
        <div className="success-grid">
          <div className="success-grid-icon">
            <MotifIcon
              iconFunction={IconoirCheckCircle}
              size="28"
              fill="#26A04B"
              strokeWidth="5px"
            />
          </div>
          <div className="success-grid-text">
            <div className="success-grid-text-title">
              Order placed successfully
            </div>
            <div className="success-grid-order-description">
              Your order has been submitted. You can review the details below.
            </div>
            {orderData.CRMSyncFailureStatus != null &&
              orderData.CRMSyncFailureStatus >= 1 && (
                <div className="success-grid-order-description order-success-description">
                  We’re currently unable to sync it with the CRM, but you can
                  retry the sync later from{" "}
                  <span
                    className="order-success-link-value"
                    onClick={() => nav("/my-workspace/my-orders")}
                  >
                    My Orders
                  </span>
                  .
                </div>
              )}
          </div>
        </div>
      </div>
      <div className="motif-container order-success-content">
        <div className="motif-row order-number-grid">
          <div className="motif-col-xs-12 motif-col-sm-12 motif-col-md-3 motif-col-lg-3 align-left-middle">
            <div className="order-number-heading">{orderData.Description}</div>
            <div className="order-number-value">{orderData.OrderId}</div>
          </div>
          <div className="motif-col-xs-12 motif-col-sm-12 motif-col-md-3 motif-col-lg-3 align-left-middle">
            <div className="order-number-heading">
              {opportunityNameLoading ? (
                <Loader />
              ) : opportunityName ? (
                opportunityName
              ) : (
                "Opportunity Code"
              )}
            </div>
            <div className="order-number-value">{orderData.OpportunityId}</div>
          </div>
          <div className="motif-col-xs-12 motif-col-sm-12 motif-col-md-3 motif-col-lg-3 align-left-middle">
            <div className="order-number-heading">Track Order in Pipeline</div>
            <div
              className="order-link-value"
              onClick={() => nav("/order-pipeline/estimates")}
            >
              Order pipeline
              <MotifIcon
                iconFunction={IconoirOpenNewWindow}
                size="13"
                fill="#35A4E8"
                className="link-icon"
              />
            </div>
          </div>
          <div className="motif-col-xs-12 motif-col-sm-12 motif-col-md-3 motif-col-lg-3 align-right-middle">
            <div className="order-number-heading right-align">Total Cost</div>
            <div className="order-number-value right-align total-cost">
              {getTotalOrderAmount(orderData)}
            </div>
            {variableCost > 0 && (
              <div className="right-align">
                <div className="variable-cost-info-grid">
                  <div className="variable-cost">
                    {variableCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}{" "}
                    Variable cost
                  </div>
                  <InfoTooltip
                    description={variableTotalInfo()}
                    info={true}
                    id="variable-total-cost-info"
                    size="14"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="motif-row order-summary">
          <div className="motif-col-lg-12 order-summary-header">
            <div className="order-summary-header-title">Order Summary</div>
            <div className="order-summary-header-description">
              Products and services you're currently using across your
              engagements
            </div>
          </div>
          <table className="motif-table-standard">
            <thead>
              <tr>
                <th className="sku-th">SKU#/ SKU Name</th>
                <th>Engagement Code</th>
                <th>Quantity</th>
                <th>Unit Cost</th>
                <th>Duration</th>
                <th>Total Cost</th>
              </tr>
            </thead>
            <tbody>
              {orderData?.Details.map((orderDataRow, index) => (
                <ProductAccordian
                  key={index}
                  skuDetails={orderDataRow.SkuDetails}
                  productName={orderDataRow.ProductName}
                  showEngagementColumn={true}
                />
              ))}
            </tbody>
          </table>
        </div>

        <div className="motif-row">
          <div className="motif-col-lg-12">
            <div className="order-placed-go-to-home">
              <Button
                className="order-placed-button"
                variant="primary"
                label="Back To Home Page"
                onClickHandler={() => {
                  nav("/");
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
